import React, {
  KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
// draft js
import { DraftHandleValue, EditorState, getDefaultKeyBinding, Modifier, SelectionState } from 'draft-js';
// draft js plugins
import Editor from '@draft-js-plugins/editor';
import { EmojiPlugin } from '@draft-js-plugins/emoji';
import createMentionPlugin, {
  MentionData,
  defaultSuggestionsFilter,
} from '@draft-js-plugins/mention';
// mui components
import { Grid } from '@mui/material';
// components
import Entry from './components/entry';
import Mention from './components/mention';
// custom hooks
import { useAppDispatch } from 'redux-store/hooks/useAppDispatch';
// slices
import { setDefaultEditorHeight, setEditorHeight } from 'redux-store/slices/editor-height-slice';
// types
import PluginEditor from '@draft-js-plugins/editor/lib/Editor';
import { IStyles } from 'utils/types';
// styles
import '@draft-js-plugins/emoji/lib/plugin.css';
import '@draft-js-plugins/mention/lib/plugin.css';
import 'draft-js/dist/Draft.css';
import './styles.css';
import { HashtagEntry } from './components/hashtag-entry';
import { Hashtag } from './components/hashtag';
import { useLazyGetHashtagSuggestionsQuery } from 'redux-store/api/activity-feed';
import { mapHashtagSuggestion } from './utils';
import { IHashtagMention } from 'redux-store/api/activity-feed/hashtags.types';

export interface ICustomMentionData extends MentionData {
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface IDraftJsInputProps {
  saveHeight?: boolean;
  isError: boolean;
  placeholder: string;
  editorState: EditorState;
  autofocus?: boolean;
  inputStyles?: IStyles;
  mentionsList?: ICustomMentionData[];
  projectId?: number;
  onChange: React.Dispatch<React.SetStateAction<EditorState>>;
  onSubmit?: () => void;
  onBlur: () => void;
  emojiPlugin: EmojiPlugin;
  enableMentions?: boolean;
  enableHashtags?: boolean;
}

const DraftJsInput: React.FC<IDraftJsInputProps> = ({
  saveHeight,
  isError,
  placeholder,
  editorState,
  inputStyles,
  autofocus,
  mentionsList,
  projectId,
  onChange,
  onBlur,
  onSubmit,
  emojiPlugin,
  enableMentions = true,
  enableHashtags = true,
}) => {
  const [getHashtagSuggestions] = useLazyGetHashtagSuggestionsQuery();

  const dispatch = useAppDispatch();
  const [open, setOpen] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState(mentionsList);

  const [openHashtag, setOpenHashtag] = useState<boolean>(false);
  const [hashtagSuggestions, setHashtagSuggestions] = useState<IHashtagMention[]>([]);

  const [emojiSuggestionsOpen, setEmojiSuggestionsOpen] = useState<boolean>(false);

  const { EmojiSuggestions } = emojiPlugin;

  const mentionPlugin = useMemo(
    () =>
      createMentionPlugin({
        mentionPrefix: '@',
        mentionTrigger: '@',
        entityMutability: 'IMMUTABLE',
        mentionComponent: Mention,
      }),
    []
  );

  const hashtagPlugin = useMemo(
    () =>
      createMentionPlugin({
        mentionPrefix: '#',
        mentionTrigger: '#',
        entityMutability: 'IMMUTABLE',
        mentionComponent: Hashtag,
      }),
    []
  );

  const { MentionSuggestions } = mentionPlugin;
  const { MentionSuggestions: HashtagSuggestions } = hashtagPlugin;

  const resizeObserver = new ResizeObserver((entries) => {
    const height = entries?.[0]?.contentRect?.height;
    if (typeof height === 'number') {
      dispatch(setEditorHeight(height));
    }
  });

  const onOpenChange = useCallback((_open: boolean) => {
    setOpen(_open);
  }, []);

  const onHashtagOpenChange = useCallback((_open: boolean) => {
    setOpenHashtag(_open);

    if (!_open) {
      setHashtagSuggestions([]);
    }
  }, []);

  const onSearchChange = useCallback(({ value }: { value: string }) => {
    setSuggestions(defaultSuggestionsFilter(value, mentionsList));
  }, []);

  const onHashtagSearchChange = useCallback(
    async ({ value }: { value: string }) => {
      try {
        const result = await getHashtagSuggestions({
          search: value?.trim() || '',
          projectId,
        }).unwrap();

        const mappedData = (result?.items || []).map(mapHashtagSuggestion);
        setHashtagSuggestions(mappedData);
      } catch (error) {
        console.error('Failed to fetch hashtag suggestions', error);
        setHashtagSuggestions([]);
      }
    },
    [getHashtagSuggestions, projectId]
  );

  const editorRef = useRef<PluginEditor>(undefined);

  const fixCursorPosition = useCallback((state: EditorState): EditorState => {
    const selection = state.getSelection();
    if (!selection.isCollapsed()) {
      return state;
    }

    const contentState = state.getCurrentContent();
    const key = selection.getAnchorKey();
    const offset = selection.getAnchorOffset();
    const block = contentState.getBlockForKey(key);

    let entityKey = null;

    if (offset < block.getLength()) {
      entityKey = block.getEntityAt(offset);
    }
    if (!entityKey && offset > 0) {
      entityKey = block.getEntityAt(offset - 1);
    }
    if (!entityKey && offset + 1 < block.getLength()) {
      entityKey = block.getEntityAt(offset + 1);
    }

    if (entityKey) {
      const entity = contentState.getEntity(entityKey);
      if (entity.getMutability() === 'IMMUTABLE') {
        let start = -1;
        let end = -1;

        for (let i = 0; i < block.getLength(); i++) {
          if (block.getEntityAt(i) === entityKey) {
            if (start === -1) start = i;
            end = i + 1; 
          } else if (start !== -1) {
            break;
          }
        }

        if (start !== -1 && end !== -1 && offset > start && offset <= end) {
          const newSelection = selection.merge({
            anchorOffset: end,
            focusOffset: end,
          }) as SelectionState;

          return EditorState.forceSelection(state, newSelection);
        }
      }
    }

    return state;
  }, []);

  const handleChange = useCallback((newState: EditorState) => {
    const fixedState = fixCursorPosition(newState);
    onChange(fixedState);
  }, [onChange, fixCursorPosition]);

  const keyBindingFn = (event: KeyboardEvent) => {
    if (open || openHashtag || emojiSuggestionsOpen) {
      return undefined;
    }
    if (event.key === 'Enter' && event.ctrlKey) {
      return 'enter_command';
    }

    const selection = editorState.getSelection();
    if (selection.isCollapsed()) {
      const contentState = editorState.getCurrentContent();
      const startKey = selection.getStartKey();
      const startOffset = selection.getStartOffset();
      const block = contentState.getBlockForKey(startKey);

      const hasEntityBefore = startOffset > 0 && !!block.getEntityAt(startOffset - 1);
      const hasEntityAt = startOffset < block.getLength() && !!block.getEntityAt(startOffset);

      if (event.key === 'ArrowLeft' && hasEntityBefore) {
        return 'arrow-left';
      }
      if (event.key === 'ArrowRight' && hasEntityAt) {
        return 'arrow-right';
      }
      if (event.key === 'Backspace') {
        if (hasEntityBefore) {
          return 'backspace';
        }
      }
      if (event.key === 'Delete' && hasEntityAt) {
        return 'delete';
      }
    }

    return getDefaultKeyBinding(event);
  };

  const handleKeyCommand = (command?: string): DraftHandleValue => {
    if (command === 'enter_command' && onSubmit) {
      onSubmit();
      return 'handled';
    }

    const selection = editorState.getSelection();

    if (!selection.isCollapsed()) {
      return 'not-handled';
    }

    const contentState = editorState.getCurrentContent();
    const startKey = selection.getStartKey();
    const startOffset = selection.getStartOffset();
    const block = contentState.getBlockForKey(startKey);

    const getEntityRange = (offset: number) => {
      if (offset < 0 || offset >= block.getLength()) {
        return null;
      }

      const entityKey = block.getEntityAt(offset);
      if (!entityKey) return null;

      const entity = contentState.getEntity(entityKey);
      if (entity.getMutability() !== 'IMMUTABLE') return null;

      let start = -1;
      let end = -1;

      for (let i = 0; i < block.getLength(); i++) {
        if (block.getEntityAt(i) === entityKey) {
          if (start === -1) start = i;
          end = i + 1;
        } else if (start !== -1) {
          break;
        }
      }

      if (start === -1 || end === -1) return null;

      return { start, end };
    };

    if (command === 'arrow-left' && startOffset > 0) {
      const entityRange = getEntityRange(startOffset - 1);
      if (entityRange) {
        onChange(
          EditorState.forceSelection(
            editorState,
            selection.merge({
              anchorOffset: entityRange.start,
              focusOffset: entityRange.start,
            }) as SelectionState
          )
        );
        return 'handled';
      }
    }

    if (command === 'arrow-right' && startOffset < block.getLength()) {
      const entityRange = getEntityRange(startOffset);
      if (entityRange) {
        onChange(
          EditorState.forceSelection(
            editorState,
            selection.merge({
              anchorOffset: entityRange.end,
              focusOffset: entityRange.end,
            }) as SelectionState
          )
        );
        return 'handled';
      }
    }

    if (command === 'backspace') {
      let entityKeyAtCursor = null;
      let entityKeysToCheck = [];

      if (startOffset > 0) {
        entityKeysToCheck.push(block.getEntityAt(startOffset - 1));
      }
      if (startOffset > 1) {
        entityKeysToCheck.push(block.getEntityAt(startOffset - 2));
      }

      for (const key of entityKeysToCheck) {
        if (key) {
          const entity = contentState.getEntity(key);
          if (entity.getMutability() === 'IMMUTABLE') {
            entityKeyAtCursor = key;
            break;
          }
        }
      }

      if (entityKeyAtCursor) {
        let entityStart = -1;
        let entityEnd = -1;

        for (let i = 0; i < block.getLength(); i++) {
          if (block.getEntityAt(i) === entityKeyAtCursor) {
            if (entityStart === -1) entityStart = i;
            entityEnd = i + 1;
          } else if (entityStart !== -1) {
            break;
          }
        }

        if (entityStart !== -1 && entityEnd !== -1) {
          const newContent = Modifier.removeRange(
            contentState,
            selection.merge({
              anchorOffset: entityStart,
              focusOffset: entityEnd,
            }) as SelectionState,
            'backward'
          );

          const newEditorState = EditorState.push(editorState, newContent, 'remove-range');

          const newSelection = selection.merge({
            anchorOffset: entityStart,
            focusOffset: entityStart,
          }) as SelectionState;

          onChange(EditorState.forceSelection(newEditorState, newSelection));
          return 'handled';
        }
      }

      return 'not-handled';
    }

    if (command === 'delete' && startOffset < block.getLength()) {
      const entityRange = getEntityRange(startOffset);
      if (entityRange) {
        const newContent = Modifier.removeRange(
          contentState,
          selection.merge({
            anchorOffset: entityRange.start,
            focusOffset: entityRange.end,
          }) as SelectionState,
          'forward'
        );

        const newEditorState = EditorState.push(editorState, newContent, 'remove-range');

        const newSelection = selection.merge({
          anchorOffset: entityRange.start,
          focusOffset: entityRange.start,
        }) as SelectionState;

        onChange(EditorState.forceSelection(newEditorState, newSelection));
        return 'handled';
      }
    }

    return 'not-handled';
  };

  useEffect(() => {
    if (autofocus) {
      onChange((prev) => {
        const withFocus = EditorState.moveFocusToEnd(prev);

        const selection = withFocus.getSelection();
        const contentState = withFocus.getCurrentContent();
        const key = selection.getAnchorKey();
        const offset = selection.getAnchorOffset();
        const block = contentState.getBlockForKey(key);

        if (offset > 0 && offset < block.getLength()) {
          const entityKey = block.getEntityAt(offset);
          if (entityKey) {
            const entity = contentState.getEntity(entityKey);
            if (entity.getMutability() === 'IMMUTABLE') {
              let end = offset;
              while (end < block.getLength() && block.getEntityAt(end) === entityKey) {
                end++;
              }

              const newSelection = selection.merge({
                anchorOffset: end,
                focusOffset: end,
              }) as SelectionState;

              return EditorState.forceSelection(withFocus, newSelection);
            }
          }
        }

        return withFocus;
      });
    }
  }, []);

  useEffect(() => {
    if (saveHeight && editorRef?.current?.editor?.editorContainer) {
      resizeObserver.observe(editorRef.current.editor.editorContainer);
    }

    return () => {
      if (saveHeight) {
        resizeObserver.disconnect();
        dispatch(setDefaultEditorHeight());
      }
    };
  }, [editorRef, saveHeight]);

  return (
    <Grid
      container
      sx={
        inputStyles ?? {
          '.editor :global(.public-DraftEditor-content)': {
            minHeight: '140px',
          },
          '.DraftEditor-editorContainer': {
            outline: isError ? '1px solid #d32f2f' : '1px solid #D0D5DD',
            fontFamily:
              "'Inter', 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', 'Twemoji', sans-serif",
            '&:hover': {
              outline: '1px solid black',
            },
            '&:focus-within': {
              outline: '2px solid #1976d2',
            },
          },
        }
      }
    >
      <Editor
        editorKey="editor"
        editorState={editorState}
        onChange={handleChange}
        placeholder={placeholder}
        plugins={[emojiPlugin, ...(enableMentions ? [mentionPlugin] : []), ...(enableHashtags ? [hashtagPlugin] : [])]}
        onBlur={onBlur}
        handleKeyCommand={handleKeyCommand}
        keyBindingFn={keyBindingFn}
        ref={editorRef}
      />
      <EmojiSuggestions
        onOpen={() => {
          setEmojiSuggestionsOpen(true);
        }}
        onClose={() => {
          setEmojiSuggestionsOpen(false);
        }}
      />
      {suggestions ? (
        <MentionSuggestions
          open={open}
          onOpenChange={onOpenChange}
          suggestions={suggestions}
          onSearchChange={onSearchChange}
          entryComponent={Entry}
        />
      ) : null}

      {enableHashtags && (
        <Grid
          sx={{
            '.mnw6qvm': {
              maxHeight: '200px !important',
              overflowY: 'auto !important',
            },
          }}
        >
          <HashtagSuggestions
            open={openHashtag}
            onOpenChange={onHashtagOpenChange}
            suggestions={hashtagSuggestions}
            onSearchChange={onHashtagSearchChange}
            entryComponent={HashtagEntry}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default memo(DraftJsInput);
